import React, { useEffect, useState } from 'react';
import './index.scss';
import {
    BoxedRow,
    ButtonFixedFooterLayout,
    ButtonPrimary,
    Divider,
    IconChevronLeftRegular,
    IconChevronRightRegular,
    NavigationBar,
    RadioButton,
    RadioGroup,
    Text1,
    Text2,
    Text3,
    Text4,
    Text6,
    Text7,
    ThemeVariant,
    skinVars,
    useElementDimensions,
} from '@telefonica/mistica';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isBrowser } from 'react-device-detect';
import { TrackingEvents } from '../../../../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../../../../utils/navigation/controlDevices';
import navigateParams from '../../../../../../utils/navigation/navigateParams';
import { Slot } from '../../../../../../models/Orders';
import { retrieveModifyAppointmentInfo, saveModifyAppointmentSelectedSlot } from '../../../appointmentStorage';
import { getDate, getIntervalText, getShortWeekDay } from '../../../slotUtils';
import BreadCrumbsWrapper from '../../../../../../components/BreadcrumbsWrapper';

export const ModifyAppointmentWeek: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { trackingScreenView } = new TrackingEvents();
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
    const { height, ref } = useElementDimensions();
    const [orderDescription, setOrderDescription] = useState<string>('');
    const [slotsByDay, setSlotsByDay] = useState<Map<string, Slot[]>>(new Map<string, Slot[]>());
    const [daysByWeek, setDaysByWeek] = useState<Map<number, string[]>>(new Map<number, string[]>());
    const [selectedWeek, setSelectedWeek] = useState<number>(0);
    const [selectedDay, setSelectedDay] = useState<string>();
    const [selectedSlot, setSelectedSlot] = useState<Slot>();
    const [params] = useSearchParams();

    useEffect(() => {
        const { orderDescription, slots } = retrieveModifyAppointmentInfo();
        setOrderDescription(orderDescription);
        initializeData(slots.slice(5)); // First five slots were displayed on previous window
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    const initializeData = (slots: Slot[]) => {
        // Initialize slots by day
        const slotsByDay = new Map<string, Slot[]>();
        slots.forEach((slot) => slotsByDay.set(getDate(slot), []));
        slots.forEach((slot) => slotsByDay.get(getDate(slot))?.push(slot));
        setSlotsByDay(slotsByDay);
        // Initialize days by week
        const daysByWeek = new Map<number, string[]>();
        const days = Array.from(slotsByDay.keys());
        for (let i = 0; i < Math.ceil(days.length / 7); i++) {
            daysByWeek.set(i, []);
            for (let j = 0; j < 7; j++) {
                if (days[i * 7 + j]) {
                    daysByWeek.get(i)?.push(days[i * 7 + j]);
                }
            }
        }
        setDaysByWeek(daysByWeek);
        setSelectedDay(daysByWeek.get(0)?.at(0)!);
    };

    const sendToNative = (url: string, type: string, title: string) => {
        const queryParams = {};
        if (navigateParams(type, url, queryParams, {}, title)) {
            navigate(`${url}`);
        }
    };

    const styleGap = () => {
        if (daysByWeek.get(selectedWeek)!.length < 7) {
            if (windowWidth >= 375 && windowWidth < 768) {
                return '20px';
            } else if (windowWidth >= 768 && windowWidth < 1024) {
                return '80px';
            } else if (windowWidth >= 1024 && windowWidth < 1220) {
                return '18px';
            } else if (windowWidth >= 1220) {
                return '48px';
            }
        }
        return 'inherit';
    };

    const getSelectedWeekInterval = () => {
        const days = daysByWeek.get(selectedWeek) || [];
        const from = days.at(0)?.substring(0, days.at(0)?.lastIndexOf('/'));
        const to = days.at(days.length - 1)?.substring(0, days.at(days.length - 1)?.lastIndexOf('/'));
        return `${from} - ${to}`;
    };

    return (
        <div className='modifyAppointmentWeek-page'>
            {slotsByDay.size > 0 && (
                <>
                    <div
                        className='modifyAppointmentWeek-page__header'
                        style={{ background: skinVars.colors.backgroundBrand }}
                    >
                        {!(isWebviewAndroid || isWebviewIOS) && (
                            <>
                                <div className='modifyAppointmentWeek-page__header-navigationBar'>
                                    <NavigationBar
                                        isInverse={true}
                                        onBack={() => {
                                            navigate(-1);
                                        }}
                                        title={t('modifyAppointment')}
                                    />
                                </div>
                                <div className='modifyAppointmentWeek-page__header-return'>
                                    <div
                                        className='modifyAppointmentWeek-page__header-return-cursor'
                                        onClick={() => {
                                            navigate(-1);
                                        }}
                                    >
                                        <div className='modifyAppointmentWeek-page__header-return-cursor-icon'>
                                            <IconChevronLeftRegular
                                                color={skinVars.colors.appBarBackground}
                                                size={20}
                                            />
                                        </div>
                                        <Text2 medium color={skinVars.colors.appBarBackground}>
                                            {t('returnResponsive')}
                                        </Text2>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className='modifyAppointmentWeek-page__header-breadcrumbs'>
                            <ThemeVariant isInverse>
                                <BreadCrumbsWrapper
                                    title={t('modifyAppointment')}
                                    breadcrumbs={[
                                        { title: t('profileOrders'), url: '/profile/orders' },
                                        { title: t('orderDetail'), url: `/order-detail?id=${params.get('id')}` },
                                    ]}
                                />
                            </ThemeVariant>
                        </div>
                        <div className='modifyAppointmentWeek-page__header-product'>
                            <Text6 color={skinVars.colors.textPrimaryInverse}>{orderDescription}</Text6>
                        </div>
                    </div>
                    <div className='modifyAppointmentWeek-page__content' ref={ref}>
                        {windowWidth >= 1024 ? (
                            <div className='modifyAppointmentWeek-page__content-web'>
                                <div className='modifyAppointmentWeek-page__content-web-left'>
                                    <div className='modifyAppointmentWeek-page__content-Headboard'>
                                        <div className='modifyAppointmentWeek-page__content-Headboard-wrapper'>
                                            <div
                                                style={{ visibility: selectedWeek === 0 ? 'hidden' : 'visible' }}
                                                className='modifyAppointmentWeek-page__content-Headboard-wrapper-chevron-left'
                                                onClick={() => {
                                                    setSelectedWeek(selectedWeek - 1);
                                                    setSelectedDay(daysByWeek.get(selectedWeek - 1)![0]);
                                                    setSelectedSlot(
                                                        slotsByDay.get(daysByWeek.get(selectedWeek - 1)![0])![0],
                                                    );
                                                }}
                                            >
                                                <IconChevronLeftRegular size={24} color={skinVars.colors.brand} />
                                            </div>
                                            <div className='modifyAppointmentWeek-page__content-Headboard-wrapper-week'>
                                                <Text7>{getSelectedWeekInterval()}</Text7>
                                            </div>
                                            <div
                                                style={{
                                                    visibility:
                                                        selectedWeek === daysByWeek.size - 1 ? 'hidden' : 'visible',
                                                }}
                                                className='modifyAppointmentWeek-page__content-Headboard-wrapper-chevron-right'
                                                onClick={() => {
                                                    setSelectedWeek(selectedWeek + 1);
                                                    setSelectedDay(daysByWeek.get(selectedWeek + 1)![0]);
                                                    setSelectedSlot(
                                                        slotsByDay.get(daysByWeek.get(selectedWeek + 1)![0])![0],
                                                    );
                                                }}
                                            >
                                                <IconChevronRightRegular size={24} color={skinVars.colors.brand} />
                                            </div>
                                        </div>
                                        <div
                                            className='modifyAppointmentWeek-page__content-Headboard-calendar'
                                            style={{
                                                gap: styleGap(),
                                                justifyContent:
                                                    daysByWeek.get(selectedWeek)?.length === 7 ? 'space-between' : '',
                                            }}
                                        >
                                            {daysByWeek.get(selectedWeek)?.map((item, index) => {
                                                const weekDay = getShortWeekDay(slotsByDay.get(item)?.at(0)!);
                                                return (
                                                    <div
                                                        className='modifyAppointmentWeek-page__content-Headboard-calendar-days'
                                                        key={index}
                                                        onClick={() => {
                                                            setSelectedDay(item);
                                                        }}
                                                    >
                                                        <div
                                                            key={index}
                                                            className='modifyAppointmentWeek-page__content-Headboard-calendar-days-wrapper'
                                                        >
                                                            <Text2
                                                                medium
                                                                color={
                                                                    selectedDay === item
                                                                        ? skinVars.colors.textPrimary
                                                                        : skinVars.colors.textSecondary
                                                                }
                                                            >
                                                                {weekDay}
                                                            </Text2>
                                                            <div
                                                                className='modifyAppointmentWeek-page__content-Headboard-calendar-days-day'
                                                                style={{
                                                                    background:
                                                                        selectedDay === item
                                                                            ? skinVars.colors.borderSelected
                                                                            : skinVars.colors.appBarBackground,
                                                                }}
                                                            >
                                                                <Text4
                                                                    regular
                                                                    color={
                                                                        selectedDay === item
                                                                            ? skinVars.colors.appBarBackground
                                                                            : skinVars.colors.textPrimary
                                                                    }
                                                                >
                                                                    {item.split('/')[0]}
                                                                </Text4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <Divider></Divider>
                                    </div>
                                    <div className='modifyAppointmentWeek-page__content-web-buttons'>
                                        <div className='modifyAppointmentWeek-page__content-web-buttons-primary'>
                                            <ButtonPrimary
                                                disabled={selectedSlot === undefined}
                                                onPress={() => {
                                                    saveModifyAppointmentSelectedSlot(selectedSlot!);
                                                    sendToNative(
                                                        '/order-detail/modify-appointment/confirm',
                                                        'modeFlow-backgroundBlue',
                                                        t('modifyAppointment'),
                                                    );
                                                }}
                                            >
                                                {t('continue')}
                                            </ButtonPrimary>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className='modifyAppointmentWeek-page__content-web-right'
                                    style={{ maxHeight: height }}
                                >
                                    <Text1 medium>{t('availableTimes')}</Text1>
                                    <RadioGroup
                                        name={'schedules'}
                                        onChange={(value) => setSelectedSlot(JSON.parse(value))}
                                        disabled={slotsByDay.get(selectedDay!) === undefined}
                                    >
                                        <div className='modifyAppointmentWeek-page__content-radioB'>
                                            {slotsByDay.get(selectedDay!)?.map((item, index) => {
                                                return (
                                                    <div
                                                        className='modifyAppointmentWeek-page__content-radioB-rows'
                                                        key={index}
                                                    >
                                                        <BoxedRow
                                                            title={''}
                                                            extra={
                                                                <div className='modifyAppointmentWeek-page__content-radioB-rows-extra'>
                                                                    <div className='modifyAppointmentWeek-page__content-radioB-rows-extra-hour'>
                                                                        <Text3 regular>{getIntervalText(item)}</Text3>
                                                                    </div>
                                                                    <Text2
                                                                        regular
                                                                        color={skinVars.colors.textSecondary}
                                                                    >
                                                                        {getDate(item)}
                                                                    </Text2>
                                                                </div>
                                                            }
                                                            right={
                                                                <RadioButton value={JSON.stringify(item)}></RadioButton>
                                                            }
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        ) : (
                            <ButtonFixedFooterLayout
                                button={
                                    <ButtonPrimary
                                        disabled={selectedSlot === undefined}
                                        onPress={() => {
                                            saveModifyAppointmentSelectedSlot(selectedSlot!);
                                            sendToNative(
                                                '/order-detail/modify-appointment/confirm',
                                                'modeFlow-backgroundBlue',
                                                t('modifyAppointment'),
                                            );
                                        }}
                                    >
                                        {t('continue')}
                                    </ButtonPrimary>
                                }
                            >
                                <div
                                    className='modifyAppointmentWeek-page__content-Headboard'
                                    style={{
                                        top: isBrowser ? '56px' : '0px',
                                    }}
                                >
                                    <div className='modifyAppointmentWeek-page__content-Headboard-wrapper'>
                                        <div
                                            style={{ visibility: selectedWeek === 0 ? 'hidden' : 'visible' }}
                                            className='modifyAppointmentWeek-page__content-Headboard-wrapper-chevron-left'
                                            onClick={() => {
                                                setSelectedWeek(selectedWeek - 1);
                                                setSelectedDay(daysByWeek.get(selectedWeek - 1)![0]);
                                                setSelectedSlot(
                                                    slotsByDay.get(daysByWeek.get(selectedWeek - 1)![0])![0],
                                                );
                                            }}
                                        >
                                            <IconChevronLeftRegular size={24} color={skinVars.colors.brand} />
                                        </div>
                                        <div className='modifyAppointmentWeek-page__content-Headboard-wrapper-week'>
                                            <Text7>{getSelectedWeekInterval()}</Text7>
                                        </div>
                                        <div
                                            style={{
                                                visibility: selectedWeek === daysByWeek.size - 1 ? 'hidden' : 'visible',
                                            }}
                                            className='modifyAppointmentWeek-page__content-Headboard-wrapper-chevron-right'
                                            onClick={() => {
                                                setSelectedWeek(selectedWeek + 1);
                                                setSelectedDay(daysByWeek.get(selectedWeek + 1)![0]);
                                                setSelectedSlot(
                                                    slotsByDay.get(daysByWeek.get(selectedWeek + 1)![0])![0],
                                                );
                                            }}
                                        >
                                            <IconChevronRightRegular size={24} color={skinVars.colors.brand} />
                                        </div>
                                    </div>
                                    <div
                                        className='modifyAppointmentWeek-page__content-Headboard-calendar'
                                        style={{
                                            gap: styleGap(),
                                            justifyContent:
                                                daysByWeek.get(selectedWeek)?.length === 7 ? 'space-between' : '',
                                        }}
                                    >
                                        {daysByWeek.get(selectedWeek)?.map((item, index) => {
                                            const weekDay = getShortWeekDay(slotsByDay.get(item)?.at(0)!);
                                            return (
                                                <div
                                                    className='modifyAppointmentWeek-page__content-Headboard-calendar-days'
                                                    key={index}
                                                    onClick={() => {
                                                        setSelectedDay(item);
                                                    }}
                                                >
                                                    <div
                                                        key={index}
                                                        className='modifyAppointmentWeek-page__content-Headboard-calendar-days-wrapper'
                                                    >
                                                        {selectedDay === item ? (
                                                            <Text1
                                                                medium
                                                                color={
                                                                    selectedDay === item
                                                                        ? skinVars.colors.textPrimary
                                                                        : skinVars.colors.textSecondary
                                                                }
                                                            >
                                                                {weekDay}
                                                            </Text1>
                                                        ) : (
                                                            <Text1
                                                                regular
                                                                color={
                                                                    selectedDay === item
                                                                        ? skinVars.colors.textPrimary
                                                                        : skinVars.colors.textSecondary
                                                                }
                                                            >
                                                                {weekDay}
                                                            </Text1>
                                                        )}
                                                        <div
                                                            className='modifyAppointmentWeek-page__content-Headboard-calendar-days-day'
                                                            style={{
                                                                background:
                                                                    selectedDay === item
                                                                        ? skinVars.colors.borderSelected
                                                                        : skinVars.colors.appBarBackground,
                                                            }}
                                                        >
                                                            <Text3
                                                                regular
                                                                color={
                                                                    selectedDay === item
                                                                        ? skinVars.colors.appBarBackground
                                                                        : skinVars.colors.textPrimary
                                                                }
                                                            >
                                                                {item.split('/')[0]}
                                                            </Text3>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <Divider></Divider>
                                </div>
                                <RadioGroup
                                    name={'schedules'}
                                    onChange={(value) => setSelectedSlot(JSON.parse(value))}
                                    disabled={slotsByDay.get(selectedDay!) === undefined}
                                >
                                    <div className='modifyAppointmentWeek-page__content-radioB'>
                                        {slotsByDay.get(selectedDay!)?.map((item, index) => {
                                            return (
                                                <div
                                                    className='modifyAppointmentWeek-page__content-radioB-rows'
                                                    key={index}
                                                >
                                                    <BoxedRow
                                                        title={''}
                                                        extra={
                                                            <div className='modifyAppointmentWeek-page__content-radioB-rows-extra'>
                                                                <div className='modifyAppointmentWeek-page__content-radioB-rows-extra-hour'>
                                                                    <Text3 regular>{getIntervalText(item)}</Text3>
                                                                </div>
                                                                <Text2 regular color={skinVars.colors.textSecondary}>
                                                                    {getDate(item)}
                                                                </Text2>
                                                            </div>
                                                        }
                                                        right={<RadioButton value={JSON.stringify(item)}></RadioButton>}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </RadioGroup>
                            </ButtonFixedFooterLayout>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ModifyAppointmentWeek;
